import { useCallback, useEffect, useRef } from 'react';
import { useConfigStore } from '../store/useConfigStore';

// Default Adsgram Block IDs (can be overridden by config)
export const ADSGRAM_REWARDED_BLOCK_ID = '18951';
export const ADSGRAM_INTERSTITIAL_BLOCK_ID = 'int-18952';

interface ShowPromiseResult {
  done: boolean;
  description: string;
  state: 'load' | 'render' | 'playing' | 'destroy';
  error: boolean;
}

interface AdController {
  show: () => Promise<ShowPromiseResult>;
}

interface AdsgramSDK {
  init: (options: { blockId: string }) => AdController;
}

interface AdexiumWidget {
  show: () => Promise<void>;
}

declare global {
  interface Window {
    Adsgram?: AdsgramSDK;
    AdexiumWidget?: {
      new (options: { wid: string; adFormat: string }): AdexiumWidget;
    };
  }
}

interface UseAdsgramProps {
  blockId: string;
  onReward?: () => void;
  onError?: (result: ShowPromiseResult) => void;
  onStart?: () => void;
}

export function useAdsgram({ blockId, onReward, onError, onStart }: UseAdsgramProps) {
  const AdControllerRef = useRef<AdController | undefined>(undefined);

  useEffect(() => {
    if (window.Adsgram) {
      AdControllerRef.current = window.Adsgram.init({ blockId });
    }
  }, [blockId]);

  const showAd = useCallback(async () => {
    if (AdControllerRef.current) {
      onStart?.();
      
      try {
        const result = await AdControllerRef.current.show();
        // User watched ad till the end or closed it (for interstitial)
        if (result.done) {
          onReward?.();
        }
        return result;
      } catch (result) {
        // User got error during playing ad
        onError?.(result as ShowPromiseResult);
        throw result;
      }
    } else {
      const errorResult: ShowPromiseResult = {
        error: true,
        done: false,
        state: 'load',
        description: 'Adsgram script not loaded',
      };
      onError?.(errorResult);
      throw errorResult;
    }
  }, [onError, onReward, onStart]);

  return showAd;
}

// Simple function to show rewarded ad
export async function showRewardedAd(): Promise<boolean> {
  // Get config values
  const config = useConfigStore.getState();
  const blockId = config.adsgram.rewardedBlockId || ADSGRAM_REWARDED_BLOCK_ID;
  
  // Check if adsgram is enabled
  if (!config.adsEnabled || !config.adsgram.enabled) {
    console.warn('Adsgram ads are disabled');
    return false;
  }

  return new Promise((resolve) => {
    if (!window.Adsgram) {
      console.error('Adsgram SDK not loaded');
      resolve(false);
      return;
    }

    const AdController = window.Adsgram.init({ blockId });
    
    AdController.show()
      .then((result) => {
        console.log('Rewarded ad completed:', result);
        resolve(result.done);
      })
      .catch((result) => {
        console.error('Rewarded ad error:', result);
        resolve(false);
      });
  });
}

// Simple function to show interstitial ad
export async function showInterstitialAd(): Promise<boolean> {
  // Get config values
  const config = useConfigStore.getState();
  const blockId = config.adsgram.interstitialBlockId || ADSGRAM_INTERSTITIAL_BLOCK_ID;
  
  // Check if adsgram is enabled
  if (!config.adsEnabled || !config.adsgram.enabled) {
    console.warn('Adsgram ads are disabled');
    return false;
  }

  // Try Adsgram first
  if (window.Adsgram) {
    return new Promise((resolve) => {
      const AdController = window.Adsgram!.init({ blockId });
      
      AdController.show()
        .then((result) => {
          console.log('Interstitial ad completed (Adsgram):', result);
          resolve(true); // Interstitial always counts as success if shown
        })
        .catch((result) => {
          console.warn('Adsgram interstitial ad error, trying Adexium:', result);
          // Try Adexium as fallback
          showAdexiumInterstitial().then(resolve).catch(() => resolve(false));
        });
    });
  } else if (window.AdexiumWidget) {
    // Adsgram not available, try Adexium directly
    return showAdexiumInterstitial();
  } else {
    console.error('Neither Adsgram nor Adexium SDK loaded');
    return false;
  }
}

// Helper function to show Adexium interstitial ad
async function showAdexiumInterstitial(): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      if (!window.AdexiumWidget) {
        console.warn('Adexium SDK not loaded');
        resolve(false);
        return;
      }

      const adexiumWidget = new window.AdexiumWidget({
        wid: '4bf11e85-b276-4a49-9c19-46313c5595e2',
        adFormat: 'interstitial'
      });

      adexiumWidget.show()
        .then(() => {
          console.log('Adexium interstitial ad completed');
          resolve(true);
        })
        .catch((error) => {
          console.warn('Adexium interstitial ad error:', error);
          resolve(false);
        });
    } catch (error) {
      console.error('Error showing Adexium ad:', error);
      resolve(false);
    }
  });
}
